/**
 * THE MATCH LOADING SCREEN: ONE ORGANIZED WAIT BETWEEN A DECK AND THE BATTLE.
 *
 * The owner, 24 September 2026: "Before the game starts: A loading screen that
 * downloads all of the assets required before the game begins." And: "The game
 * loads a lot from the server just in time, at the moment it's needed, instead
 * of preloading more." And: "a loading screen in itself is a sacrifice, it's
 * also best to avoid it."
 *
 * Measured before this existed (Chrome 1400x640, 110 ms RTT, the snappiness
 * meter): after the deck press NOTHING visible happened for 3.3 s — the server
 * was building the match — and then the board appeared with the card back, the
 * opponent's fan and both deck piles still downloading in front of the player.
 * The first-visit guest tutorial showed the same backs loading, and the two
 * deck canvases arriving ~220 ms after the board.
 *
 * So the press now paints this screen in the same task, and everything the
 * board paints that is known before the server answers — the stock card back
 * (the AI's and every default back), the player's own back, the four card
 * frames, the card and board faces — is fetched AND decoded while the server
 * works. No card picture is: the server shuffles and deals, so no card of the
 * deck is known to be on the first screen until it says which (owner, 26
 * September 2026: "does the game load all card assets when this is prob not
 * needed, since most cards won't be drawn?"). What only the server can name
 * (the starting hand, the opponent's summoner and back) is warmed when
 * `gameStarted` lands, and the board is built UNDER the screen. The screen
 * lifts in the same task as the first screen presents (the mulligan's reveal,
 * or the board itself), so the battle appears whole, in one frame.
 *
 * Three rules keep it honest:
 *   - The progress bar is the real warm set: each item is a real download,
 *     decode or server step, a failure counts as settled, and the bar never
 *     runs backwards when the server's answer adds new work.
 *   - Nothing after `gameStarted` may wait longer than the mulligan's own
 *     1500 ms reveal cap. The server starts the 30 s mulligan clock when it
 *     activates the match (game/managers/gameManager.js activateGameStart), so
 *     a longer client wait would silently spend the player's decision time.
 *     The heavy lifting therefore happens BEFORE the server answers.
 *   - It is skipped wherever it would be a sacrifice: a board that is already
 *     on screen (a rematch, a reconnect) is never covered, the boot veil is
 *     the loading screen when one is up, and a warm second match adds only
 *     the few frames its board takes to paint from cache after the server's
 *     answer — no download stands between that answer and the mulligan.
 *
 * The arrival names what it waited on last in User Timing, as the lobby's
 * does: `match-loading:answer` (the server's answer), `match-loading:board:
 * <ready|cap>` and `match-loading:lift:<reason>` (what presented the battle —
 * the mulligan's reveal, its cap, the board, a backstop).
 *
 * Contract: tests/unit/ui/matchLoading.test.js; doctrine: docs/asset-loading.md
 * (law 3, "Match start").
 */

import { i18n } from '../i18n.js';
import { onLanguageChange } from '../i18n-events.js';
import { BootVeil } from './boot-veil.js';
import { CardBackRaster } from './card-back-raster.js';
import { CardbackArt } from './cardback-art.js';
import { EnemyFan } from './enemy-fan.js';

const MatchLoading = (function () {
    'use strict';

    const SCREEN_ID = 'match-loading';
    /* The mulligan's own reveal cap (mulligan.js MULLIGAN_REVEAL_CAP_MS): the
       most a match start may already cost the server's mulligan clock. */
    const ARRIVAL_CAP_MS = 1500;
    /* Releases the cover if no first screen ever reports in — the mulligan's
       staging fallback fires at its cap + 250 ms, so this stays behind it. */
    const ARRIVAL_BACKSTOP_MS = ARRIVAL_CAP_MS + 500;
    /* The longest a start can still be pending before it answers or gives up
       (socket-handler.js): the preflight's 20 s cap (the connection inside it
       gives up at 10 s), the 30 s start acknowledgement, and for a room join
       that reached another worker, a reconnect (10 s) and a second 30 s
       acknowledgement. Each ends in the lobby's own cancel or in the board;
       the backstop only covers a path that forgot to, so it outlasts them all
       rather than lift onto a start still pending (audit of round 16: a slow
       reconnect outlasted the 35 s it used to be). */
    const START_WORST_CASE_MS = 20000 + 30000 + 10000 + 30000;
    const SCREEN_BACKSTOP_MS = START_WORST_CASE_MS + 5000;
    const FADE_MS = 300;

    const STOCK_CARDBACK = '/assets/images/cardback.webp';
    /* The frames are `.card-front::before` backgrounds (cards.css) and are
       preloaded in index.html; these must match cards.css byte for byte
       (tests/unit/ui/matchLoading.test.js). */
    const CARD_FRAME_ART = Object.freeze([
        '/assets/images/cardfront_creature.webp?v=2',
        '/assets/images/cardfront_spell.webp?v=2',
        '/assets/images/cardfront_creature_stellar.webp',
        '/assets/images/cardfront_spell_stellar.webp',
    ]);
    /* Every face a card and the board print (cards.css @font-face). */
    const BATTLE_FONTS = Object.freeze([
        ['600 16px "Cinzel"'],
        ['700 16px "Cinzel"'],
        ['400 16px "Spectral"'],
        ['500 16px "Spectral"'],
        ['500 16px "Moonfall Mana Numerals"', '0123456789'],
    ]);

    const decodedArt = new Map();
    let session = null;
    let hideTimer = null;
    /* What the connection notice says now (socket-handler.js), or null. */
    let connectionMessage = null;
    /* A start held while Moonfall updates (holdForUpdate), or null. */
    let updateHold = null;
    /* Elements this session has seen laid out (hasBox). */
    let boxesSeen = new WeakSet();

    function uiText(key, fallback) {
        const translated = i18n?.t?.(key);
        return translated && translated !== key ? translated : fallback;
    }

    function prefersReducedMotion() {
        return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    }

    function settled(promise) {
        return Promise.resolve(promise).then(() => undefined, () => undefined);
    }

    function mark(name) {
        try { performance.mark(`match-loading:${name}`); } catch (_) { /* no User Timing */ }
    }

    /* A fire-and-forget step (a progress tick, a board check, the lift) is
       reported when it throws, never left as an unhandled rejection. Found 26
       September 2026: a warm that settled after its page was torn down (a
       jsdom test page) threw from the bar's repaint, and the unhandled
       rejection ended the whole jest process, so every suite in its batch
       lost its result. */
    function detach(promise, step) {
        void promise.catch((error) => {
            try {
                console.warn(`[MatchLoading] ${step} failed`, error);
            } catch (_) { /* nowhere left to report it */ }
        });
    }

    /** @returns {{ promise: Promise<any>, resolve: (value?: any) => void }} */
    function deferred() {
        let resolve = /** @type {(value?: any) => void} */ (() => {});
        const promise = new Promise((done) => { resolve = done; });
        return { promise, resolve };
    }

    function fillOf(screen) {
        return /** @type {HTMLElement|null} */ (screen.querySelector('.match-loading__fill'));
    }

    /** Resolves once `url` is decoded (or has failed) — never rejects. */
    function decoded(url) {
        const cached = decodedArt.get(url);
        if (cached) return cached;
        const pending = new Promise((resolve) => {
            try {
                const image = new Image();
                image.decoding = 'async';
                image.src = url;
                if (typeof image.decode === 'function') {
                    Promise.resolve(image.decode()).then(() => resolve(true), () => resolve(false));
                } else {
                    image.onload = () => resolve(true);
                    image.onerror = () => resolve(false);
                }
            } catch (_) {
                resolve(false);
            }
        });
        decodedArt.set(url, pending);
        // A failure is not an enduring truth: the next match asks again.
        void pending.then((ok) => { if (!ok && decodedArt.get(url) === pending) decodedArt.delete(url); });
        return pending;
    }

    function loadFont(descriptor, text) {
        try {
            const fonts = document.fonts;
            if (!fonts || typeof fonts.load !== 'function') return Promise.resolve();
            return settled(text ? fonts.load(descriptor, text) : fonts.load(descriptor));
        } catch (_) {
            return Promise.resolve();
        }
    }

    function warmStockCardback() {
        try {
            return settled(CardBackRaster?.warm?.(CardbackArt?.STOCK_URL || STOCK_CARDBACK));
        } catch (_) {
            return Promise.resolve();
        }
    }

    /**
     * The battle's shared chrome, fetched and decoded: the stock card back
     * into the card-back raster cache the deck piles and the fan paint from,
     * the four card frames, and the faces. Idempotent — each piece is cached
     * by its owner — so intent signals may call it freely.
     */
    function warmChrome() {
        // The screen itself is part of the match: built (hidden) on the first
        // sign of intent, its logo decoded, so the press paints it whole.
        ensureScreen();
        return [
            warmStockCardback(),
            ...CARD_FRAME_ART.map((url) => decoded(url)),
            Promise.all(BATTLE_FONTS.map(([descriptor, text]) => loadFont(descriptor, text))),
        ];
    }

    // ---- the screen ------------------------------------------------------

    function ensureScreen() {
        let screen = document.getElementById(SCREEN_ID);
        if (screen) return screen;
        screen = document.createElement('div');
        screen.id = SCREEN_ID;
        screen.className = 'match-loading';
        screen.hidden = true;
        screen.setAttribute('aria-busy', 'false');

        // The boot veil's own logo, so it is already in the cache; decoded now
        // so the screen's first frame carries it.
        const logo = document.createElement('img');
        logo.className = 'match-loading__logo';
        logo.alt = '';
        logo.width = 1312;
        logo.height = 736;
        logo.src = 'assets/images/logo.webp';
        try {
            void Promise.resolve(logo.decode?.()).catch(() => {});
        } catch (_) { /* painted on its own when it cannot be decoded ahead */ }

        const label = document.createElement('p');
        label.className = 'match-loading__label';
        label.setAttribute('role', 'status');
        label.setAttribute('data-i18n', 'game.loadingBattle');

        const bar = document.createElement('div');
        bar.className = 'match-loading__bar';
        bar.setAttribute('role', 'progressbar');
        bar.setAttribute('aria-valuemin', '0');
        bar.setAttribute('aria-valuemax', '100');
        bar.setAttribute('aria-valuenow', '0');
        bar.setAttribute('data-i18n-aria-label', 'game.loadingProgress');
        const fill = document.createElement('span');
        fill.className = 'match-loading__fill';
        bar.appendChild(fill);

        /* The connection's line, under the bar and out of the column's flow,
           so saying it moves nothing. Not a live region: the socket handler's
           own notice under this screen already is one. */
        const notice = document.createElement('p');
        notice.className = 'match-loading__notice';
        notice.setAttribute('aria-hidden', 'true');
        const progress = document.createElement('div');
        progress.className = 'match-loading__progress';
        progress.append(bar, notice);

        /* Cancel, in the screen's corner, for a start held while Moonfall
           updates (holdForUpdate). Built with the screen, hidden and out of
           the column's flow, so holding a start only reveals it. */
        const cancel = document.createElement('button');
        cancel.type = 'button';
        cancel.className = 'match-loading__cancel';
        cancel.tabIndex = -1;
        cancel.setAttribute('data-i18n', 'common.cancel');
        cancel.addEventListener('click', () => updateHold?.cancel());

        screen.append(logo, label, progress, cancel);
        document.body.appendChild(screen);
        return screen;
    }

    /* A DROP WHILE THE SERVER BUILDS THE MATCH IS SAID ON THE SCREEN. The
       bar stands still while the connection is down, and the socket
       handler's "Connection lost. Reconnecting…" notice sat under this
       opaque screen, unseen (audit of round 16). Its words are shown here
       while the notice is up; the line keeps them as it fades out. */
    function paintConnection(screen = document.getElementById(SCREEN_ID)) {
        const notice = screen?.querySelector('.match-loading__notice');
        if (!notice) return;
        if (connectionMessage && notice.textContent !== connectionMessage) notice.textContent = connectionMessage;
        /* While a start is held the status line already says Moonfall is
           updating; the drop is that update switching versions. */
        notice.classList.toggle('is-shown', connectionMessage !== null && updateHold === null);
    }

    /* A HELD START IS SAID ON THE STATUS LINE (holdForUpdate). The line keeps
       the box it was painted in (match-loading.css, `.is-holding`): its height
       is locked to one line of its own type, and the message, set a little
       smaller, is centred on it. Words that need two lines spill evenly into
       the margins above and below; past what those margins can take the size
       steps down until they fit, so neither the logo nor the bar moves. The
       bar rests, because nothing is loading. */
    const HOLD_MIN_FONT_PX = 9;
    const HOLD_CLEARANCE_PX = 4;

    function fitHeldMessage(screen) {
        const label = screen?.querySelector('.match-loading__label');
        const words = label?.querySelector('.match-loading__update');
        const logo = screen?.querySelector('.match-loading__logo');
        const progress = screen?.querySelector('.match-loading__progress');
        if (!words || !logo || !progress) return;
        words.style.fontSize = '';
        const above = label.offsetTop - (logo.offsetTop + logo.offsetHeight);
        const below = progress.offsetTop - (label.offsetTop + label.offsetHeight);
        const room = label.offsetHeight + 2 * Math.min(above, below) - HOLD_CLEARANCE_PX;
        if (!(room > 0)) return;
        let size = parseFloat(getComputedStyle(words).fontSize);
        while (words.offsetHeight > room && size > HOLD_MIN_FONT_PX) {
            size = Math.max(HOLD_MIN_FONT_PX, size - 0.5);
            words.style.fontSize = `${size}px`;
        }
    }

    function paintHold(screen = document.getElementById(SCREEN_ID)) {
        const label = /** @type {HTMLElement | null} */ (screen?.querySelector('.match-loading__label'));
        const cancel = /** @type {HTMLButtonElement | null} */ (screen?.querySelector('.match-loading__cancel'));
        if (!label || !cancel) return;
        if (updateHold) {
            if (!screen.classList.contains('is-holding')) {
                label.style.setProperty('--match-loading-label-height', `${label.offsetHeight}px`);
                // The words carry their own key, so a language or viewport
                // change re-translates them and never restores the old line.
                label.removeAttribute('data-i18n');
                const words = document.createElement('span');
                words.className = 'match-loading__update';
                words.setAttribute('data-i18n', 'connection.serverUpdating');
                label.replaceChildren(words);
                screen.classList.add('is-holding');
            }
            const words = label.querySelector('.match-loading__update');
            if (words.textContent !== updateHold.message) words.textContent = updateHold.message;
            if (cancel.textContent !== updateHold.cancelLabel) cancel.textContent = updateHold.cancelLabel;
            cancel.classList.add('is-shown');
            cancel.tabIndex = 0;
            fitHeldMessage(screen);
        } else if (screen.classList.contains('is-holding')) {
            screen.classList.remove('is-holding');
            label.style.removeProperty('--match-loading-label-height');
            label.setAttribute('data-i18n', 'game.loadingBattle');
            label.textContent = uiText('game.loadingBattle', 'Preparing the battle…');
            cancel.classList.remove('is-shown');
            cancel.tabIndex = -1;
        }
        paintConnection(screen);
    }

    /** The connection notice changed (socket-handler.js): its words, or null once it is gone. */
    function showConnectionState(message) {
        connectionMessage = typeof message === 'string' && message.trim() ? message : null;
        paintConnection();
    }

    function renderProgress(current) {
        const screen = document.getElementById(SCREEN_ID);
        if (!current || !screen || current.counting) return;
        const value = current.total > 0 ? current.done / current.total : 0;
        // New work never pulls the bar back; finishing work moves it forward.
        current.shown = Math.max(current.shown, Math.min(1, value));
        const fill = fillOf(screen);
        if (fill) fill.style.transform = `scaleX(${current.shown})`;
        screen.querySelector('[role="progressbar"]')
            ?.setAttribute('aria-valuenow', String(Math.round(current.shown * 100)));
    }

    /* Opaque from its first frame. The deck selector closes in the task of
       the press, so an entrance fade would flash the lobby behind it; a cut to
       the finished loading screen answers the press on the very next frame. */
    function showScreen() {
        if (hideTimer !== null) {
            clearTimeout(hideTimer);
            hideTimer = null;
        }
        const screen = ensureScreen();
        const label = screen.querySelector('.match-loading__label');
        if (label) label.textContent = uiText('game.loadingBattle', 'Preparing the battle…');
        screen.querySelector('[role="progressbar"]')
            ?.setAttribute('aria-label', uiText('game.loadingProgress', 'Loading progress'));
        const fill = fillOf(screen);
        if (fill && screen.hidden) fill.style.transform = 'scaleX(0)';
        paintConnection(screen);
        screen.classList.remove('is-leaving');
        screen.hidden = false;
        screen.setAttribute('aria-busy', 'true');
        return screen;
    }

    function hideScreen() {
        const screen = document.getElementById(SCREEN_ID);
        if (!screen || screen.hidden) return;
        screen.setAttribute('aria-busy', 'false');
        if (hideTimer !== null) clearTimeout(hideTimer);
        hideTimer = null;
        if (prefersReducedMotion()) {
            screen.hidden = true;
            return;
        }
        screen.classList.add('is-leaving');
        hideTimer = setTimeout(() => {
            hideTimer = null;
            screen.hidden = true;
            screen.classList.remove('is-leaving');
        }, FADE_MS);
    }

    // ---- the session -----------------------------------------------------

    function track(promise, owner = session) {
        if (!owner || owner !== session) return false;
        owner.total += 1;
        detach(settled(promise).then(() => {
            owner.done += 1;
            if (owner === session) renderProgress(owner);
        }), 'progress');
        renderProgress(owner);
        return true;
    }

    /* `pressed`: the session opens on the player's own press, whose start
       request leaves in that same task. That is the bar's first real step, so
       its fill paints with the screen instead of arriving a warm later (round
       15: 153 ms after the screen on Chrome desktop, 233-397 ms on the WebKit
       phone, read by the meter as a piece arriving late). */
    function openSession({ showsScreen, pressed = false }) {
        boxesSeen = new WeakSet();
        const current = {
            done: pressed ? 1 : 0,
            total: pressed ? 1 : 0,
            shown: 0,
            showsScreen,
            chrome: warmChrome(),
            server: deferred(),
            board: deferred(),
            arrival: null,
            backstop: null,
            /* The session's work is counted WHOLE before the bar is drawn
               (review of wave D, 28 September 2026): drawn as each step was
               counted, the press's step read as 1 of 2 — the bar opened half
               full, and stood still while the warms it had not yet counted
               landed. */
            counting: true,
        };
        session = current;
        current.chrome.forEach((promise) => track(promise, current));
        track(current.server.promise, current);
        track(current.board.promise, current);
        current.counting = false;
        armBackstop(current);
        return current;
    }

    function armBackstop(current) {
        clearTimeout(current.backstop);
        current.backstop = setTimeout(() => {
            if (session === current) close('backstop');
        }, SCREEN_BACKSTOP_MS);
    }

    function close(reason) {
        const current = session;
        if (!current) return false;
        session = null;
        endUpdateHold({ cancelled: true });
        if (current.arrival) mark(`lift:${reason}`);
        clearTimeout(current.backstop);
        if (current.arrival) {
            clearTimeout(current.arrival.backstop);
            clearTimeout(current.arrival.cap);
            if (current.arrival.frame !== null) window.cancelAnimationFrame?.(current.arrival.frame);
            current.arrival.presented.resolve(reason);
            current.arrival.boardReady?.resolve(reason);
        }
        current.server.resolve();
        current.board.resolve();
        if (current.showsScreen) hideScreen();
        return true;
    }

    /**
     * The player has committed to a match (a deck press, the tutorial, a room
     * join). Paints the screen in this task and starts the pre-game warm.
     * Returns whether a screen of its own is showing.
     *
     * The chosen deck (`{ deck }`, from the lobby) is deliberately not warmed:
     * its hand is dealt from a shuffle the server has not made yet, so a
     * picture fetched now is most likely one the first screen never shows,
     * and on a slow link it would still be downloading beside the hand when
     * the answer lands.
     *
     * @param {{ deck?: object|null }} [_intent]
     */
    function begin(_intent = {}) {
        if (session) return session.showsScreen;
        const showsScreen = !BootVeil?.isUp?.();
        if (showsScreen) showScreen();
        const current = openSession({ showsScreen, pressed: true });
        renderProgress(current);
        return showsScreen;
    }

    /** The start failed, stalled or was abandoned: take the screen down. */
    function cancel() {
        return close('cancelled');
    }

    function endUpdateHold({ cancelled = false } = {}) {
        const hold = updateHold;
        if (!hold) return;
        updateHold = null;
        hold.stopRefit();
        if (cancelled) hold.cancel();
        paintHold();
    }

    /**
     * A START HELD WHILE MOONFALL UPDATES (ui/server-update-wait.js).
     *
     * A release drains the server and refuses new battles until the new
     * version answers, which can take minutes. The screen stays up past its
     * backstop, says why on its status line (paintHold) and offers Cancel in
     * its corner; neither moves anything. Pressing Cancel only tells the hold:
     * the start that owns the screen takes it down, as for any failed start.
     * Taking the screen down ends the hold as cancelled.
     *
     * Returns null when this session shows no screen of its own (none open,
     * or the boot veil is the loading screen): there is nowhere to say it.
     *
     * @param {{ message: string, cancelLabel: string }} copy
     * @returns {{ cancelled: Promise<void>, isCancelled: () => boolean, release: () => void } | null}
     */
    function holdForUpdate({ message, cancelLabel }) {
        const current = session;
        if (!current || !current.showsScreen) return null;
        endUpdateHold({ cancelled: true });
        let isCancelled = false;
        let resolveCancelled;
        const cancelled = new Promise((resolve) => { resolveCancelled = resolve; });
        const refit = () => {
            if (updateHold === hold) fitHeldMessage(document.getElementById(SCREEN_ID));
        };
        const stopLanguageRefit = onLanguageChange(refit);
        const hold = {
            message: String(message || ''),
            cancelLabel: String(cancelLabel || ''),
            cancel() {
                if (isCancelled) return;
                isCancelled = true;
                resolveCancelled();
            },
            stopRefit() {
                stopLanguageRefit();
                window.removeEventListener('resize', refit);
            },
        };
        updateHold = hold;
        window.addEventListener('resize', refit, { passive: true });
        clearTimeout(current.backstop);
        paintHold();
        return {
            cancelled,
            isCancelled: () => isCancelled,
            release() {
                if (updateHold !== hold) return;
                endUpdateHold();
                if (session === current) armBackstop(current);
            },
        };
    }

    /**
     * The player's own card back for this match, read fresh by the start
     * preflight: warmed while the server builds the match, and counted in the
     * bar while a session is open. Never rejects.
     */
    function warmCardback(slug) {
        const normalized = typeof slug === 'string' && slug.trim() ? slug.trim() : 'default';
        // The stock back is already part of every session's chrome.
        if (normalized === 'default') return warmStockCardback().then(() => true);
        const warm = Promise.resolve()
            .then(() => CardbackArt?.resolve?.(normalized))
            .then((url) => (url ? Promise.resolve(CardBackRaster?.warm?.(url)).then(() => true) : false))
            .catch(() => false);
        track(warm);
        if (session) session.chrome.push(warm);
        return warm;
    }

    // ---- the arrival -----------------------------------------------------

    /* Something with no box is not on screen, and the raster that paints it
       waits for a box — so it is nothing to hold the board for. A LAYOUT READ:
       the check below asks it last, and only of a pile or a fan that is not
       painted yet; and a box an element had once in this arrival it keeps
       until the board presents, so it is measured once, not on every frame
       (28 September 2026: a fan sharpening through most of the arrival cost
       182 ms of forced layouts on the 4x phone). No box is re-read: an
       element that is not laid out yet may be the next frame. Kept per
       session (`boxesSeen`, reset by openSession): a later match measures
       its own board. */
    function hasBox(element) {
        if (!element) return false;
        if (boxesSeen.has(element)) return true;
        const has = Number(element.offsetWidth) > 0;
        if (has) boxesSeen.add(element);
        return has;
    }

    function deckPainted(deck) {
        if (
            deck.dataset.cardBackRasterSource
            && deck.querySelector(':scope > canvas[data-card-back-raster="true"]')
        ) return true;
        return !hasBox(deck);
    }

    function portraitSettled(portrait) {
        const state = portrait.dataset.portraitResolutionState;
        if (state === 'unavailable') return true;
        return state === 'resolved' && portrait.dataset.portraitQuality !== 'pending';
    }

    function opponentBacksSettled(hand) {
        if (hand.dataset.cardbackArtKind === 'custom-pending') return false;
        const backs = [...hand.querySelectorAll(':scope > .card-back')];
        if (!backs.length || backs.every((back) => back.dataset.fanTextureKey)) return true;
        // A fan that could not be sharpened keeps its exact CSS back.
        if (!EnemyFan?.isSharpening?.()) return true;
        return !hasBox(backs[0]);
    }

    function cardsSettled() {
        const mulligan = document.body.classList.contains('mulligan-phase');
        const zones = mulligan
            ? ['#player-board', '#opponent-board', '#opponent-hand']
            : ['#player-hand', '#player-board', '#opponent-board', '#opponent-hand'];
        if (document.querySelector(
            zones.map((zone) => `${zone} .card-image > .card-art-loading`).join(', '),
        )) return false;
        /* A resting hand card is shown through its rest view, drawn a few
           frames after its art settles (card-rest-view.js). A board presented
           in between showed its hand a beat late: the hand's cards, costs,
           names and art read as pop-ins 128 ms after a mid-match reload's
           board (snappy meter, 4 October 2026). The card says so on itself,
           so this is read without a layout, like everything asked first.
           Its frame is shown reduced the same way, and says so the same way. */
        return mulligan || !document.querySelector(
            '#player-hand > .card:is([data-card-art-rest="pending"], [data-card-frame-rest="pending"])',
        );
    }

    /* Everything the board shows is final: both piles carry their settled
       card-back texture, both summoners their portrait, the opponent's fan its
       back, and every face-up card its art.

       CHEAPEST FIRST, LAYOUT LAST. This runs on every frame of the arrival,
       while the hand's rasters and the portraits' samples keep the layout
       dirty; a box read there forces a style and layout pass of the whole
       battle. Traced 28 September 2026 on the Chrome phone seat (4x CPU):
       reading both piles' boxes first, every frame, cost 320 ms of the match
       bar's main thread while a portrait — known from its data attributes
       alone — was what the board was really waiting for. So the portraits
       and the cards are asked first, and a box only of a pile or a fan that
       is not painted yet. */
    function boardSettled() {
        for (const id of ['player-portrait', 'opponent-portrait']) {
            const portrait = document.getElementById(id);
            if (portrait && !portraitSettled(portrait)) return false;
        }
        if (!cardsSettled()) return false;
        const hand = document.getElementById('opponent-hand');
        if (hand?.dataset.cardbackArtKind === 'custom-pending') return false;
        for (const id of ['player-deck', 'opponent-deck']) {
            const deck = document.getElementById(id);
            if (deck && !deckPainted(deck)) return false;
        }
        return !hand || opponentBacksSettled(hand);
    }

    /**
     * The server has answered with the match. Call BEFORE the game view is
     * switched in. Returns null when nothing needs covering (the board is
     * already on screen), otherwise `{ presented, capMs }`: `presented`
     * resolves when the first screen is up, for a boot veil to hold on.
     */
    function arrive() {
        const view = document.getElementById('game-view');
        const bootVeil = Boolean(BootVeil?.isUp?.());
        if (!session) {
            if (!bootVeil && (!view || document.body.classList.contains('game-active'))) return null;
            openSession({ showsScreen: !bootVeil });
        }
        const current = session;
        if (current.arrival) return { presented: current.arrival.presented.promise, capMs: ARRIVAL_BACKSTOP_MS };
        // A boot veil that has gone by now covers nothing: this screen does.
        if (!bootVeil) current.showsScreen = true;
        if (current.showsScreen) showScreen();
        current.server.resolve();
        mark('answer');
        current.arrival = {
            presented: deferred(),
            boardReady: null,
            cap: null,
            frame: null,
            startedAt: Date.now(),
            backstop: setTimeout(() => {
                if (session === current) presented('backstop');
            }, ARRIVAL_BACKSTOP_MS),
        };
        renderProgress(current);
        return { presented: current.arrival.presented.promise, capMs: ARRIVAL_BACKSTOP_MS };
    }

    /**
     * Resolves ('ready' or 'cap') once the board is whole, within what is left
     * of the arrival cap; null when no arrival is pending, so a board that is
     * already on screen gains no wait.
     */
    function whenBoardReady() {
        const current = session;
        const arrival = current?.arrival;
        if (!arrival) return null;
        if (arrival.boardReady) return arrival.boardReady.promise;
        const gate = deferred();
        arrival.boardReady = gate;
        let chromeSettled = false;
        let finished = false;
        detach(Promise.all(current.chrome).then(() => {
            chromeSettled = true;
            check();
        }), 'board check');
        const finish = (outcome) => {
            if (finished || arrival.boardReady !== gate) return;
            finished = true;
            mark(`board:${outcome}`);
            clearTimeout(arrival.cap);
            if (arrival.frame !== null) window.cancelAnimationFrame?.(arrival.frame);
            arrival.frame = null;
            current.board.resolve();
            gate.resolve(outcome);
        };
        function check() {
            if (finished) return;
            if (chromeSettled && boardSettled()) {
                finish('ready');
                return;
            }
            if (arrival.frame === null && typeof window.requestAnimationFrame === 'function') {
                arrival.frame = window.requestAnimationFrame(() => {
                    arrival.frame = null;
                    check();
                });
            }
        }
        arrival.cap = setTimeout(
            () => finish('cap'),
            Math.max(0, ARRIVAL_CAP_MS - (Date.now() - arrival.startedAt)),
        );
        check();
        return gate.promise;
    }

    /**
     * The first screen is presenting (the mulligan revealed its hand, or the
     * board is whole): lift the cover in this same task.
     */
    function presented(reason = 'presented') {
        if (!session?.arrival) return false;
        return close(reason);
    }

    /**
     * Arm the arrival's own ending. A mulligan presents itself (its reveal
     * calls `presented`); any other first screen is the board.
     */
    function settle(firstScreen) {
        if (!session?.arrival || firstScreen === 'mulligan') return;
        const current = session;
        const boardReady = whenBoardReady();
        if (!boardReady) return;
        detach(boardReady.then((outcome) => {
            if (session === current) presented(outcome === 'cap' ? 'cap' : 'board');
        }), 'lift');
    }

    return {
        ARRIVAL_BACKSTOP_MS,
        ARRIVAL_CAP_MS,
        CARD_FRAME_ART,
        FADE_MS,
        SCREEN_BACKSTOP_MS,
        arrive,
        begin,
        cancel,
        holdForUpdate,
        isActive: () => session !== null,
        presented,
        settle,
        showConnectionState,
        track: (promise) => track(promise),
        warmCardback,
        warmChrome,
        whenBoardReady,
    };
})();

export { MatchLoading };
